import MagnifyingGlassIcon from "@heroicons/react/16/solid/MagnifyingGlassIcon";
import XMarkIcon from "@heroicons/react/16/solid/XMarkIcon";
import Briefcase from "@heroicons/react/20/solid/BriefcaseIcon";
import MapPinIcon from "@heroicons/react/20/solid/MapPinIcon";
import SparklesIcon from "@heroicons/react/20/solid/SparklesIcon";
import Badge from "@mui/material/Badge";
import ButtonBase from "@mui/material/ButtonBase";
import InputBase from "@mui/material/InputBase";
import { styled as styling } from "@mui/material/styles";
import Color from "../../../assets/colors";
import FilterSVG from "../../../assets/svg/filter-icon.svg";
import InternshipSVG from "../../../assets/svg/internship-icon.svg";

export const Wrapper = styling("div")({
  position: "relative",
  width: "100%",
});

export const SearchOptionsWrapper = styling("div")({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "0.375rem",

  height: "100%",
  width: "100%",
  padding: "0.625rem 0 0.325rem",

  "@media (max-width: 1279.9px)": {
    padding: "1rem 1.5rem 0",
  },

  "@media (max-width: 640px)": {
    padding: "0",
    flexDirection: "column",
    alignItems: "stretch",
    gap: "0.75rem",
  },
});

export const SearchBarWrapper = styling("div")((props) => ({
  backgroundColor: Color.white,
  color: Color.darkGrey,
  border: `1px solid ${Color.secondaryGrey}`,
  borderRadius: "8px",

  display: "flex",
  alignItems: "center",
  gap: "0.375rem",

  height: "36px",
  width: "100%",
  flex: "1 1 auto",
  minWidth: "0px",
  padding: "0.375rem 0.5rem 0.375rem 1rem",

  transition: "all 0.05s ease-in-out",

  ...(props.$active && {
    border: `1px solid ${Color.hiredlyPurple}`,
    boxShadow: `0px 0px 0px 1px ${Color.hiredlyPurple}`,
  }),

  ...(props.disabled && {
    pointerEvents: "none",
    opacity: "0.5",
  }),

  "@media (max-width: 640px)": {
    backgroundColor: Color.lightPurple,
    border: `1px solid ${Color.hiredlyPurple}`,
    padding: "0.5rem 0.75rem 0.5rem 0.9rem",
    gap: "0.5rem",
  },
}));

export const SearchDropdownContainer = styling("div")((props) => ({
  position: "absolute",
  left: 0,
  top: "calc(100% + 0.5rem)",
  zIndex: 20,

  backgroundColor: Color.white,
  borderRadius: "8px",
  border: `2px solid ${Color.hiredlyPurple}`,
  boxShadow: "0px 4px 19px rgba(0, 0, 0, 0.25)",

  width: "100%",
  padding: "0.5rem",
  maxWidth: "100%",
}));

export const SearchSuggestionContainer = styling("div")({
  color: Color.textHighEmphasis,
  borderRadius: "0.5rem",

  fontSize: "0.875rem",
  fontWeight: "400",
  textAlign: "left",

  marginBottom: "5px",
  padding: "0.25rem 0.5rem",
  cursor: "pointer",

  ":hover": {
    backgroundColor: Color.lightGrey,
    cursor: "pointer",
  },
});

export const SearchInput = styling(InputBase)({
  fontSize: "0.875rem",

  width: "100%",
  padding: "0",
});

export const ClearButton = styling(ButtonBase)({
  height: "24px",
  maxWidth: "24px",
  width: "100%",
});

export const SearchButton = styling(ButtonBase)({
  backgroundColor: Color.hiredlyPurple,
  color: Color.white,
  borderRadius: "4px",

  height: "24px",
  maxWidth: "24px",
  width: "100%",
  margin: "5px !important",

  "@media (max-width: 640px)": {
    height: "36px",
    maxWidth: "36px",
    margin: "0 !important",
  },
});

export const FilterButton = styling(ButtonBase)((props) => ({
  backgroundColor: Color.white,
  color: Color.darkGrey,
  border: `1px solid ${Color.secondaryGrey}`,
  borderRadius: "8px",

  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "0.5rem",

  fontFamily: "Inter",
  fontSize: "0.875rem",
  lineHeight: "24px",
  letterSpacing: "0.15px",
  textDecoration: "none !important",
  textDecorationLine: "none !important",
  textUnderlinePosition: "unset !important",

  height: "36px",
  width: "fit-content",
  padding: "0 0.625rem",
  whiteSpace: "nowrap",

  transition: "all 0.15s ease-in-out",

  "&:hover": {
    color: Color.textMediumEmphasis,
    border: `1px solid ${Color.textMediumEmphasis}`,
    boxShadow: `0px 0px 0px 0.5px ${Color.textMediumEmphasis}`,
  },

  ...(props.$active && {
    backgroundColor: Color.lightHiredlyPurple,
    color: Color.hiredlyPurple,
    border: `1px solid ${Color.hiredlyPurple}`,
    boxShadow: `0px 0px 0px 1px ${Color.hiredlyPurple}`,

    "&:hover": {
      backgroundColor: "rgba(115, 95, 180, 1)",
      color: Color.white,
      border: `1px solid ${Color.darkPurple}`,
      boxShadow: `0px 0px 0px 1px ${Color.darkPurple}`,
    },
  }),

  "&:disabled": {
    pointerEvents: "none",
    opacity: "0.5",
  },

  "@media (max-width: 640px)": {
    width: "100%",
  },
}));

export const BadgeStyled = styling(Badge)({
  "& .MuiBadge-badge": {
    height: "1.5rem",
    width: "1.5rem",
    borderRadius: "0.25rem",

    fontSize: "0.75rem",
    fontWeight: "400",
  },

  "@media (max-width: 640px)": {
    flex: "1",
    minWidth: "fit-content",
    width: "100%",
  },
});

// Icon Components //
export const SearchIcon = styling(MagnifyingGlassIcon)({
  height: "16px",
  width: "16px",
});

export const ClearIcon = styling(XMarkIcon)({
  height: "16px",
  width: "16px",
});

export const FilterIcon = styling(FilterSVG)({
  height: "20px",
  width: "20px",
  padding: "0.25rem",
});

export const InternshipIcon = styling(InternshipSVG)({
  height: "20px",
  width: "20px",
});

export const JobsForYouButton = styling(ButtonBase)((props) => ({
  backgroundColor: Color.white,
  color: Color.darkGrey,
  border: `1px solid ${Color.secondaryGrey}`,
  borderRadius: "8px",

  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "0.5rem",

  fontFamily: "Inter",
  fontSize: "0.875rem",
  lineHeight: "24px",
  letterSpacing: "0.15px",
  textDecoration: "none !important",
  textDecorationLine: "none !important",
  textUnderlinePosition: "unset !important",

  height: "36px",
  width: "fit-content",
  padding: "0 0.625rem",
  whiteSpace: "nowrap",

  transition: "all 0.15s ease-in-out",

  "&:hover": {
    color: Color.textMediumEmphasis,
    border: `1px solid ${Color.textMediumEmphasis}`,
    boxShadow: `0px 0px 0px 0.5px ${Color.textMediumEmphasis}`,
  },

  ...(props.$active && {
    backgroundColor: Color.lightHiredlyPurple,
    color: Color.hiredlyPurple,
    border: `1px solid ${Color.hiredlyPurple}`,
    boxShadow: `0px 0px 0px 1px ${Color.hiredlyPurple}`,

    "&:hover": {
      backgroundColor: "#735FB4",
      color: Color.white,
      border: `1px solid ${Color.darkPurple}`,
      boxShadow: `0px 0px 0px 1px ${Color.darkPurple}`,
    },
  }),

  "&:disabled": {
    pointerEvents: "none",
    opacity: "0.5",
  },

  "@media (max-width: 640px)": {
    width: "100%",
  },
}));

export const FypIcon = styling(SparklesIcon)({
  height: "20px",
  width: "20px",

  "@media (max-width: 1279px)": {
    height: "16px",
    width: "16px",
  },
});

export const BriefcaseIcon = styling(Briefcase)({
  height: "20px",
  width: "20px",

  "@media (max-width: 1279px)": {
    height: "16px",
    width: "16px",
  },
});

export const MapPinIconStyled = styling(MapPinIcon)({
  height: "20px",
  width: "20px",

  "@media (max-width: 1279px)": {
    height: "16px",
    width: "16px",
  },
});

export const LocationBarWrapper = styling("div")((props) => ({
  backgroundColor: Color.white,
  color: Color.darkGrey,
  border: `1px solid ${Color.secondaryGrey}`,
  borderRadius: "8px",

  display: "flex",
  alignItems: "center",
  gap: "0.375rem",

  height: "36px",
  width: "240px",
  padding: "0.375rem 0.75rem",

  transition: "all 0.05s ease-in-out",

  ...(props.$active && {
    border: `1px solid ${Color.hiredlyPurple}`,
    boxShadow: `0px 0px 0px 1px ${Color.hiredlyPurple}`,
  }),

  ...(props.disabled && {
    pointerEvents: "none",
    opacity: "0.5",
  }),

  "@media (max-width: 1279.9px)": {
    width: "100%",
  },
}));

export const LocationInput = styling(InputBase)({
  fontSize: "0.875rem",

  width: "100%",
  minWidth: 0,
  padding: 0,

  "& .MuiInputBase-input": {
    minWidth: 0,
  },
});

export const UnifiedSearchBar = styling("div")((props) => ({
  backgroundColor: Color.white,
  color: Color.darkGrey,
  border: `1px solid ${Color.secondaryGrey}`,
  borderRadius: "8px",

  display: "flex",
  alignItems: "center",

  height: "36px",
  width: "100%",
  ...(props.disabled && {
    pointerEvents: "none",
    opacity: "0.5",
  }),
}));

export const VerticalDivider = styling("div")({
  width: "1px",
  height: "24px",
  backgroundColor: Color.secondaryGrey,
});

export const SearchBarFlat = styling("div")({
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  flex: "1 1 0",
  minWidth: 0,
  padding: "0 10px",
  height: "100%",
  borderRadius: "8px",
  border: "1px solid transparent",
  position: "relative",
  transition: "all 0.05s ease-in-out",

  "&:focus-within": {
    border: `1px solid ${Color.hiredlyPurple}`,
    boxShadow: `0px 0px 0px 1px ${Color.hiredlyPurple}`,
  },
});

export const LocationBarFlat = styling("div")({
  display: "flex",
  alignItems: "center",
  gap: "0.1rem",
  flex: "1 1 0",
  minWidth: 0,
  padding: "0 10px",
  height: "100%",
  borderRadius: "8px",
  border: "1px solid transparent",
  transition: "all 0.05s ease-in-out",

  "& > button:last-of-type": {
    marginLeft: "auto",
  },

  "& > svg:first-of-type": {
    marginRight: "0.25rem",
  },

  "&:focus-within": {
    border: `1px solid ${Color.hiredlyPurple}`,
    boxShadow: `0px 0px 0px 1px ${Color.hiredlyPurple}`,
  },
});

export const LocationCount = styling("span")({
  backgroundColor: Color.lightHiredlyPurple,
  color: Color.hiredlyPurple,
  border: `1px solid ${Color.hiredlyPurple}`,
  borderRadius: "4px",
  width: "25px",
  height: "25px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",

  fontSize: "0.75rem",
  fontWeight: 700,
  lineHeight: "16px",
  whiteSpace: "nowrap",
});

export const MobileSearchContainer = styling("div")({
  display: "flex",
  flexDirection: "column",
  gap: "0.75rem",
  width: "100%",
});

export const MobileHeaderRow = styling("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",

  "@media (max-width: 640px)": {
    borderBottom: `2px solid ${Color.black}`,
    padding: "0 0.25rem 0",
  },
});

export const MobileTitle = styling("div")((props) => ({
  display: "flex",
  alignItems: "center",
  gap: "0.4rem",
  fontWeight: 700,
  fontSize: "0.95rem",
  color: props.$active ? Color.textHighEmphasis : Color.darkGrey,

  "@media (max-width: 640px)": {
    padding: "0.75rem 2rem",
    borderRadius: "10px 10px 0 0",
    backgroundColor: props.$active ? Color.backgroundGrey : "transparent",
    border: props.$active
      ? `2px solid ${Color.black}`
      : "2px solid transparent",
    borderBottom: "0px",
    marginBottom: "-2px",
    color: props.$active ? Color.hiredlyPurple : Color.darkGrey,
    textDecoration: props.$active ? "underline" : "none",
    textUnderlinePosition: "from-font",
  },
}));

export const MobileActionsRow = styling("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "0.5rem",
  width: "100%",
  marginTop: "0.35rem",
});

export const MobileFilterWrapper = styling("div")({
  display: "flex",
  justifyContent: "center",
  paddingTop: "0.5rem",
  margin: "0.5rem 0.75rem",
});

export const IconActionButton = styling(ButtonBase)((props) => ({
  backgroundColor: props.$filled ? Color.hiredlyPurple : Color.white,
  color: props.$filled ? Color.white : Color.hiredlyPurple,
  border: `1px solid ${Color.hiredlyPurple}`,
  borderRadius: "10px",
  height: "36px",
  width: "100%",
  minWidth: "36px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,

  ...(props.$active && {
    backgroundColor: Color.lightHiredlyPurple,
    color: Color.hiredlyPurple,
    boxShadow: `0 0 0 1px ${Color.hiredlyPurple}`,
  }),

  "&:hover": {
    backgroundColor: props.$filled
      ? Color.darkPurple
      : Color.lightHiredlyPurple,
    color: Color.white,
  },

  "&:disabled": {
    pointerEvents: "none",
    opacity: "0.5",
  },
}));

export const JobsForYouLinkButton = styling(ButtonBase)((props) => ({
  backgroundColor: "transparent",
  color: props.$active ? Color.hiredlyPurple : Color.darkGrey,
  border: "none",
  display: "flex",
  alignItems: "center",
  gap: "0.4rem",
  fontWeight: 700,
  fontSize: "0.95rem",
  padding: 0,
  textDecoration: props.$active ? "underline" : "none",
  textUnderlinePosition: "from-font",

  "&:hover": {
    color: Color.hiredlyPurple,
  },

  "&:disabled": {
    pointerEvents: "none",
    opacity: "0.5",
  },

  "@media (max-width: 640px)": {
    padding: "0.75rem 2rem",
    borderRadius: "10px 10px 0 0",
    backgroundColor: props.$active ? Color.backgroundGrey : "transparent",
    border: props.$active
      ? `2px solid ${Color.black}`
      : "2px solid transparent",
    borderBottom: "0px",
    marginBottom: "-2px",
    color: props.$active ? Color.hiredlyPurple : Color.darkGrey,
    textDecoration: props.$active ? "underline" : "none",
    textUnderlinePosition: "from-font",
  },
}));
